import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Plus, ShieldCheck, KeyRound, Users, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { SECTIONS } from "@/components/admin/schemas";
import { AdminForm } from "@/components/admin/AdminForm";
import { AdminTable } from "@/components/admin/AdminTable";
import { TeamPanel } from "@/components/admin/TeamPanel";
import { InvitesPanel } from "@/components/admin/InvitesPanel";
import { AttendeesDialog } from "@/components/admin/AttendeesDialog";
import { CaptionDialog } from "@/components/admin/CaptionDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const PasswordDialog = ({ open, onClose }) => {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated.");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update password.");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">Change admin password</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <input data-testid="pw-current" type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} className="w-full border border-[color:var(--line)] rounded-xl px-4 py-2.5 text-sm" />
          <input data-testid="pw-new" type="password" placeholder="New password (min 8 chars)" value={nw} onChange={(e) => setNw(e.target.value)} className="w-full border border-[color:var(--line)] rounded-xl px-4 py-2.5 text-sm" />
          <button data-testid="pw-save-btn" className="btn-pill btn-primary w-full justify-center">Update password</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("events");
  const [view, setView] = useState("upcoming");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [attendeesFor, setAttendeesFor] = useState(null);
  const [captionFor, setCaptionFor] = useState(null);
  const section = SECTIONS[tab] || SECTIONS.events;
  const isPanel = tab === "team" || tab === "invites";

  const load = useCallback(() => {
    if (tab === "team" || tab === "invites") return;
    api.get(`/admin/${tab}`).then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [tab]);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-[color:var(--ink-2)]">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (user.role !== "admin")
    return (
      <div className="min-h-[60vh] grid place-items-center text-center px-6" data-testid="admin-forbidden">
        <div>
          <h2 className="font-display text-4xl">Back of house is team only.</h2>
          <p className="mt-3 text-[color:var(--ink-2)]">Signed in as {user.email}. Ask an admin to grant you access.</p>
          <Link to="/" className="btn-pill btn-outline mt-6 inline-flex">Back to the coast</Link>
        </div>
      </div>
    );

  const visible = tab === "events" ? items.filter((i) => (view === "past" ? i.is_past : !i.is_past)) : items;
  const drafts = items.filter((i) => i.published === false).length;

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/${tab}/${deleting.id}`);
      toast.success("Deleted — removed from the live site.");
      setDeleting(null);
      load();
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  const duplicate = async (it) => {
    try {
      const { data } = await api.post(`/admin/${tab}/${it.id}/duplicate`);
      toast.success("Duplicated as a hidden draft — edit and publish when ready.");
      load();
      setEditing(data);
    } catch { toast.error("Couldn't duplicate."); }
  };

  const reviewBusiness = async (bizId) => {
    setTab("businesses");
    const { data } = await api.get("/admin/businesses");
    setItems(data);
    const b = data.find((x) => x.id === bizId);
    if (b) setEditing(b);
  };


  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-12 pb-24" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.2em] font-bold text-[color:var(--coral)]">
            <ShieldCheck size={14} /> Back of house
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 tracking-tight">Manage the coast.</h1>
          <p className="text-[color:var(--ink-2)] mt-3">Anything you publish here goes live on the site instantly.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPwOpen(true)} data-testid="admin-change-password-btn" className="btn-pill btn-ghost"><KeyRound size={15} /> Password</button>
          {!isPanel && <button onClick={() => setEditing({})} data-testid="admin-add-btn" className="btn-pill btn-primary"><Plus size={16} /> Add {section.singular}</button>}
        </div>
      </div>

      {stats && (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="admin-stats">
          {[["events", "Events"], ["past_events", "Past events"], ["experiences", "Experiences"], ["businesses", "Listings"], ["guides", "Guides"], ["ads", "Sponsored ads"], ["drafts", "Drafts"], ["team", "Admins"]].map(([k, l]) => (
            <button type="button" key={k} onClick={() => k === "drafts" ? null : k === "team" ? setTab("team") : setTab(k === "past_events" ? "events" : k)} className="bg-white rounded-2xl border border-[color:var(--line)] p-4 text-left hover:border-[color:var(--ocean)] transition-colors">
              <div className="font-display text-3xl text-[color:var(--ocean)]">{stats[k]}</div>
              <div className="text-xs uppercase tracking-wider text-[color:var(--ink-2)] font-bold mt-1">{l}</div>
            </button>
          ))}
        </div>
      )}

      <div className="mt-10 flex flex-wrap gap-2">
        {Object.entries(SECTIONS).map(([k, s]) => (
          <button key={k} onClick={() => { setTab(k); setView("upcoming"); }} data-testid={`admin-tab-${k}`} className={`chip ${tab === k ? "chip-active" : ""}`}>{s.label}</button>
        ))}
        <button onClick={() => setTab("team")} data-testid="admin-tab-team" className={`chip ${tab === "team" ? "chip-active" : ""}`}><Users size={12} /> Team</button>
        <button onClick={() => setTab("invites")} data-testid="admin-tab-invites" className={`chip ${tab === "invites" ? "chip-active" : ""}`}><Link2 size={12} /> Business invites</button>
      </div>

      {!isPanel && drafts > 0 && (
        <div className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 inline-block" data-testid="admin-drafts-note">
          {drafts} draft{drafts > 1 ? "s" : ""} hidden from the public site — tick "Published" to go live.
        </div>
      )}

      {tab === "events" && (
        <div className="mt-5 inline-flex rounded-full border border-[color:var(--line)] bg-white p-1 text-sm">
          {[["upcoming", "Upcoming · live"], ["past", "Past · hidden from site"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`admin-view-${k}`} className={`px-4 py-1.5 rounded-full transition-colors ${view === k ? "bg-[color:var(--ocean)] text-white" : "text-[color:var(--ink-2)]"}`}>{l}</button>
          ))}
        </div>
      )}

      <div className="mt-6">
        {tab === "team" ? <TeamPanel /> : tab === "invites" ? <InvitesPanel onReview={reviewBusiness} /> : (
          <AdminTable sectionKey={tab} section={section} items={visible} onEdit={setEditing} onDelete={setDeleting}
            onAttendees={setAttendeesFor} onCaption={setCaptionFor} onDuplicate={duplicate} />
        )}
      </div>

      <AttendeesDialog event={attendeesFor} onClose={() => setAttendeesFor(null)} />
      <CaptionDialog item={captionFor} kind={section.singular} onClose={() => setCaptionFor(null)} />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">{editing?.id ? `Edit ${section.singular}` : `New ${section.singular}`}</DialogTitle>
          </DialogHeader>
          {editing && (
            <AdminForm section={section} sectionKey={tab} item={editing.id ? editing : null} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete “{deleting?.[section.titleKey]}”?</AlertDialogTitle>
            <AlertDialogDescription>This removes it from the live site immediately and can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="admin-delete-confirm" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
